import { withEntityTitle } from '@/components/workflows/docProvenance';
import { sealBlockHtml, SEAL_STYLE_CSS, type SealContext } from '@/components/workflows/sealModel';
import { needsAuthorityChecklist, type ExitWorkflowData } from './exitContent';
import type { SigningMode } from '../signingModel';

/**
 * DRAFT document builders for the "Conclude a Membership" workflow.
 *
 * Every paper here is produced as a clearly marked DRAFT and waits on approval
 * before this workflow goes live. Plain spoken register, no dashes, no internal
 * codes, and true dates only.
 */

export const EXIT_FORMATS_APPROVED = true;

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function longDate(value?: string): string {
  const d = value ? new Date(value) : new Date();
  const safe = Number.isNaN(d.getTime()) ? new Date() : d;
  return safe.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const BASE_STYLE = `
  @page { size: letter; margin: 1in; }
  body { font-family: Cambria, Georgia, serif; font-size: 12pt; line-height: 1.6; color: #1a1a1a; }
  h1 { font-size: 16pt; text-align: center; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; }
  h2 { font-size: 12pt; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 26px; }
  .subtitle { text-align: center; font-style: italic; margin-bottom: 26px; }
  .draft { border: 1px solid #8a6d3b; background: #fdf6e3; color: #6b5320; padding: 10px 14px; margin-bottom: 22px; font-size: 10pt; text-align: center; letter-spacing: 0.04em; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 11pt; }
  th, td { border: 1px solid #bbb; padding: 8px 10px; text-align: left; vertical-align: top; }
  th { background: #f3f1ec; font-weight: bold; }
  .sig { margin-top: 46px; }
  .sig-line { border-bottom: 1px solid #333; width: 320px; height: 34px; margin-bottom: 4px; }
  .sig-label { font-size: 10pt; }
  ol li, ul li { margin-bottom: 8px; }
  .note { font-size: 10pt; font-style: italic; color: #444; margin-top: 26px; }
  ${SEAL_STYLE_CSS}
`;

function page(title: string, subtitle: string, inner: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><title>${esc(title)}</title><style>${BASE_STYLE}</style></head>
<body>
  <div class="draft">DRAFT format. This wording has not yet been approved. Do not sign this page until the approved format is in place.</div>
  <h1>${esc(title)}</h1>
  <p class="subtitle">${esc(subtitle)}</p>
  ${inner}
  <p class="note">The date on this page is the true date it is signed.</p>
</body>
</html>`;
}


/** Renders the seal only when it is actually applied. Otherwise nothing at all. */
function sealSpot(ctx: SealContext): string {
  return sealBlockHtml(ctx);
}

export interface ExitBuildContext {
  data: ExitWorkflowData;
  associationName: string;
  associationOfficerRole: string;
  date?: string;
  /** True when the Association keeps a seal and the steward applied it. */
  sealEnabled?: boolean;
  /** The rendered seal markup. */
  sealSvg?: string;
}

export interface ExitOutput {
  key: string;
  title: string;
  html: string;
  requiresSignature: boolean;
  signing: SigningMode;
  defaultInk?: boolean;
  signingReason?: string;
}

function memberName(ctx: ExitBuildContext): string {
  return esc(ctx.data.member_name || "[Member's name]");
}

function sigBlock(label: string): string {
  return `<div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(label)}</div>
    <div class="sig-line" style="margin-top:34px; width:200px"></div>
    <div class="sig-label">Date signed</div>
  </div>`;
}

export function buildWrittenWithdrawalHtml(ctx: ExitBuildContext): string {
  const effective = longDate(ctx.data.effective_date);
  const inner = `
  <p>I, ${memberName(ctx)}, withdraw from Membership in ${esc(ctx.associationName)}, effective ${esc(effective)}. I understand that my contributions remain given, that I hold no interest or claim in the Association's holdings, and that the Covenant of Privacy survives my departure in full.</p>
  ${sigBlock('Departing member')}`;
  return page('Written Withdrawal', 'One page, signed by the departing member', inner);
}

export function buildAcknowledgingResolutionHtml(ctx: ExitBuildContext): string {
  const { data } = ctx;
  const effective = longDate(data.effective_date);
  const inner = `
  <p>${esc(ctx.associationName)} takes this action on ${esc(longDate(ctx.date))}.</p>
  <h2>The action</h2>
  <ul>
    <li>The Membership of ${memberName(ctx)} is concluded, effective ${esc(effective)}.</li>
    <li>The roster is updated to show the Membership concluded as of that date.</li>
    <li>Contributions given remain given. The departing member holds no interest or claim in the holdings of the Association.</li>
    <li>The Covenant of Privacy survives the departure in full, for the Association and for the departing member alike.</li>
  </ul>
  ${data.gratitude_line ? `<h2>Recorded with thanks</h2><p>${esc(data.gratitude_line)}</p>` : ''}
  ${sigBlock(`${ctx.associationOfficerRole}, for ${ctx.associationName}`)}
  ${sealSpot(ctx)}`;
  return page('Acknowledging Resolution', 'The Association records the conclusion of a Membership', inner);
}

export function buildAuthorityRemovalChecklistHtml(ctx: ExitBuildContext): string {
  const { data } = ctx;
  const bank = data.bank_name ? esc(data.bank_name) : '[Name of the bank]';
  const rows: string[] = [];
  const bankingApplies = data.held_banking === 'yes' || data.passing_authorities === 'yes';
  const officeApplies = data.held_office === 'yes' || data.passing_authorities === 'yes';

  if (bankingApplies) {
    rows.push(
      `<tr><td>Remove ${memberName(ctx)} as a signer and as an authorized user at ${bank}. Do this within the same week.</td><td></td><td></td></tr>`,
      `<tr><td>Collect and cancel any cards, tokens, or checks issued to them by ${bank}.</td><td></td><td></td></tr>`,
      `<tr><td>Remove their online banking access and confirm the removal in writing from ${bank}.</td><td></td><td></td></tr>`,
    );
  }
  if (officeApplies) {
    rows.push(
      `<tr><td>Conclude the office per the resignation instrument, and file the signed instrument with the record.</td><td></td><td></td></tr>`,
      `<tr><td>Update the officer list and any place the office is named to outside parties.</td><td></td><td></td></tr>`,
    );
  }
  rows.push(
    `<tr><td>Remove portal access, shared logins, and any keys or codes held by them.</td><td></td><td></td></tr>`,
    `<tr><td>File this completed checklist with the record.</td><td></td><td></td></tr>`,
  );

  const inner = `
  <p>Prepared on ${esc(longDate(ctx.date))} for ${esc(ctx.associationName)}.</p>
  <p>Each line gets done, then dated and initialed by the person who did it.</p>
  <table>
    <thead><tr><th>What to do</th><th>Date done</th><th>Initials</th></tr></thead>
    <tbody>${rows.join('')}</tbody>
  </table>
  ${sigBlock(`${ctx.associationOfficerRole}, for ${ctx.associationName}`)}`;
  return page('Authority Removal Checklist', 'Authorities come off the same week', inner);
}

export function buildPrivacySurvivalLetterHtml(ctx: ExitBuildContext): string {
  const inner = `
  <p>Dear ${memberName(ctx)},</p>
  <p>Your Membership is concluded, and we want to send you off with one plain reminder and one warm word. The Covenant of Privacy you kept while you were with us continues now that you have gone. What you learned about other members, about their households, and about the work of this Association stays with you and goes no further. That promise runs both ways: we hold your privacy the same way, for as long as we exist.</p>
  <p>Thank you for the time you gave us. You leave with what you came with: yourself, your dignity, and our good will. We wish you every good thing ahead, and the door of ordinary friendship stays open.</p>
  ${sigBlock(`${ctx.associationOfficerRole}, for ${ctx.associationName}`)}`;
  return page('Privacy Survival Letter', 'A short letter to the departing member', inner);
}

export function buildMemorialMinuteEntryHtml(ctx: ExitBuildContext): string {
  const { data } = ctx;
  const passing = longDate(data.passing_date);
  const inner = `
  <p>Entered in the minutes of ${esc(ctx.associationName)} on ${esc(longDate(ctx.date))}.</p>
  <h2>With honor</h2>
  <p>The Association records the passing of ${memberName(ctx)} on ${esc(passing)}, and pauses to honor a life held among us.</p>
  ${data.remembrance_line ? `<p>${esc(data.remembrance_line)}</p>` : ''}
  <h2>The roster</h2>
  <p>The roster shows the Membership of ${memberName(ctx)} concluded as of ${esc(passing)}.</p>
  <h2>What Membership is</h2>
  <p>Membership is personal. It does not descend. No estate, heir, or personal representative acquires any interest or claim in the Association or its holdings by reason of the passing. Contributions given remain given.</p>
  ${sigBlock(`${ctx.associationOfficerRole}, for ${ctx.associationName}`)}`;
  return page('Memorial Minute Entry', 'A record of a Membership concluded by death', inner);
}

function buildExitOutputsInternal(ctx: ExitBuildContext): ExitOutput[] {
  const { data } = ctx;
  if (data.contested !== 'no') return [];
  const outputs: ExitOutput[] = [];

  if (data.ending === 'withdrawal') {
    outputs.push({
      key: 'exit_written_withdrawal',
      title: 'Written Withdrawal',
      html: buildWrittenWithdrawalHtml(ctx),
      requiresSignature: true,
      signing: 'stewards_choice',
      defaultInk: true,
    });
    outputs.push({
      key: 'exit_acknowledging_resolution',
      title: 'Acknowledging Resolution',
      html: buildAcknowledgingResolutionHtml(ctx),
      requiresSignature: true,
      signing: 'stewards_choice',
      defaultInk: false,
    });
    if (needsAuthorityChecklist(data)) {
      outputs.push({
        key: 'exit_authority_removal_checklist',
        title: 'Authority Removal Checklist',
        html: buildAuthorityRemovalChecklistHtml(ctx),
        requiresSignature: false,
        signing: 'stewards_choice',
      defaultInk: false,
      });
    }
    outputs.push({
      key: 'exit_privacy_survival_letter',
      title: 'Privacy Survival Letter',
      html: buildPrivacySurvivalLetterHtml(ctx),
      requiresSignature: true,
      signing: 'stewards_choice',
      defaultInk: false,
    });
    return outputs;
  }

  if (data.ending === 'passing') {
    outputs.push({
      key: 'exit_memorial_minute_entry',
      title: 'Memorial Minute Entry',
      html: buildMemorialMinuteEntryHtml(ctx),
      requiresSignature: true,
      signing: 'stewards_choice',
      defaultInk: false,
    });
    if (needsAuthorityChecklist(data)) {
      outputs.push({
        key: 'exit_authority_removal_checklist',
        title: 'Authority Removal Checklist',
        html: buildAuthorityRemovalChecklistHtml(ctx),
        requiresSignature: false,
        signing: 'stewards_choice',
      defaultInk: false,
      });
    }
  }

  return outputs;
}

/**
 * Every generated paper carries only the entity's identity. The HTML title
 * becomes "Document title, Entity name" so the file and the PDF metadata name
 * the Association, not the portal.
 */
export function buildExitOutputs(ctx: ExitBuildContext): ExitOutput[] {
  const entityName = ctx.associationName;
  return buildExitOutputsInternal(ctx).map((output) => ({ ...output, html: withEntityTitle(output.html, entityName) }));
}
